/**
 * Broker Tier Configuration — Single Source of Truth
 *
 * Thresholds based on cumulative capital raised by the broker.
 * All components must import from this file.
 */

export const BROKER_TIERS = [
  {
    key: 'bronze',
    name: 'Bronze',
    threshold: 0,
    rateMultiplier: 0.50,
    rateOfUnitValue: 1.0,
    displayRate: 2.92,
    color: 'text-orange-400',
    bg: 'bg-orange-400/10',
    bgColor: 'bg-orange-400/10',
    border: 'border-orange-400/30',
    borderColor: 'border-orange-400/30',
    icon: '🥉',
  },
  {
    key: 'silver',
    name: 'Silver',
    threshold: 2500000,
    rateMultiplier: 0.60,
    rateOfUnitValue: 1.2,
    displayRate: 3.50,
    color: 'text-gray-300',
    bg: 'bg-gray-300/10',
    bgColor: 'bg-gray-300/10',
    border: 'border-gray-300/30',
    borderColor: 'border-gray-300/30',
    icon: '🥈',
  },
  {
    key: 'gold',
    name: 'Gold',
    threshold: 7500000,
    rateMultiplier: 0.70,
    rateOfUnitValue: 1.4,
    displayRate: 4.08,
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    bgColor: 'bg-yellow-400/10',
    border: 'border-yellow-400/30',
    borderColor: 'border-yellow-400/30',
    icon: '🥇',
  },
  {
    key: 'platinum',
    name: 'Platinum',
    threshold: 22500000,
    rateMultiplier: 0.85,
    rateOfUnitValue: 1.7,
    displayRate: 4.96,
    color: 'text-cyan-400',
    bg: 'bg-cyan-400/10',
    bgColor: 'bg-cyan-400/10',
    border: 'border-cyan-400/30',
    borderColor: 'border-cyan-400/30',
    icon: '💎',
  },
  {
    key: 'diamond',
    name: 'Diamond',
    threshold: 67500000,
    rateMultiplier: 1.0,
    rateOfUnitValue: 2.0,
    displayRate: 5.83,
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    bgColor: 'bg-purple-400/10',
    border: 'border-purple-400/30',
    borderColor: 'border-purple-400/30',
    icon: '👑',
  },
];

/** Returns the tier object for a given cumulative capital amount */
export function getCurrentTier(cumulativeCapital) {
  for (let i = BROKER_TIERS.length - 1; i >= 0; i--) {
    if (cumulativeCapital >= BROKER_TIERS[i].threshold) {
      return BROKER_TIERS[i];
    }
  }
  return BROKER_TIERS[0];
}

/** Returns the next tier (or null if already Diamond) */
export function getNextTier(cumulativeCapital) {
  const current = getCurrentTier(cumulativeCapital);
  const idx = BROKER_TIERS.indexOf(current);
  return idx < BROKER_TIERS.length - 1 ? BROKER_TIERS[idx + 1] : null;
}

/** Broker-facing rate for a deal (used by DealCard) */
export function getBrokerFacingRate(tier, maxDistributable) {
  return maxDistributable * tier.rateMultiplier;
}

/** Commission calculation (used by EarningsCalculator) */
export function calculateCommission(tier, unitValue, capitalTarget, investmentAmount) {
  const fractionOfTranche = investmentAmount / capitalTarget;
  return (tier.rateOfUnitValue / 100) * unitValue * fractionOfTranche;
}

/** Rate of capital raised for display (used by EarningsCalculator) */
export function getRateOfCapitalRaised(tier, unitValue, capitalTarget) {
  const multiplier = unitValue / capitalTarget;
  return tier.rateOfUnitValue * multiplier;
}
